import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { PieChart, TrendingDown, TrendingUp, Sparkles, Tag, Wallet } from "lucide-react";
import BudgetsDialog from "./BudgetsDialog";

const fmtEur = (v) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
    .format(Number(v) || 0);

// Stable categorical palette inspired by dataviz best practices —
// avoids "AI slop" purple gradients. Up to 8 distinct hues + gray fallback.
const PALETTE = [
  "#60A5FA", // blue-400
  "#34D399", // emerald-400
  "#FBBF24", // amber-400
  "#F87171", // rose-400
  "#A78BFA", // violet-400
  "#22D3EE", // cyan-400
  "#FB923C", // orange-400
  "#F472B6", // pink-400
  "#94A3B8", // slate-400 (fallback)
];

const monthsOptions = [
  { value: 1, label_ca: "Aquest mes", label_es: "Este mes" },
  { value: 3, label_ca: "Trimestre", label_es: "Trimestre" },
  { value: 12, label_ca: "12 mesos", label_es: "12 meses" },
];

const SIZE = 200;
const STROKE = 28;
const RADIUS = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

export default function ExpenseCategoryWidget() {
  const { lang } = useI18n();
  const [months, setMonths] = useState(1);
  const [data, setData] = useState({ rows: [], total_current: 0 });
  const [hoverIdx, setHoverIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBudgets, setShowBudgets] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/received-invoices/expenses-by-category", {
        params: { months },
      });
      setData(data);
    } catch {
      setData({ rows: [], total_current: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [months]); // eslint-disable-line

  // Compute donut segments
  const segments = useMemo(() => {
    if (!data.total_current) return [];
    let acc = 0;
    return data.rows.map((r, idx) => {
      const length = (r.total / data.total_current) * CIRC;
      const offset = -acc;
      acc += length;
      return {
        ...r,
        idx,
        color: PALETTE[idx % PALETTE.length],
        dashLength: length,
        dashGap: CIRC - length,
        offset,
      };
    });
  }, [data]);

  const center = SIZE / 2;
  const hovered = hoverIdx !== null ? segments[hoverIdx] : null;

  return (
    <div className="card-surface p-5" data-testid="expense-category-widget">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center">
            <PieChart size={14} className="text-violet-300" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">
              {lang === "es" ? "Despesa por categoría" : "Despesa per categoria"}
            </h3>
            <div className="text-[10px] text-slate-500">
              {lang === "es" ? "Distribución del gasto y variación" : "Distribució de la despesa i variació"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowBudgets(true)}
            className="px-2 py-1 rounded text-[11px] font-medium border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 inline-flex items-center gap-1 mr-1"
            data-testid="category-budgets-btn"
            title={lang === "es" ? "Presupuestos por categoría" : "Pressupostos per categoria"}
          >
            <Wallet size={11} />
            {lang === "es" ? "Presupuestos" : "Pressupostos"}
          </button>
          {monthsOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setMonths(opt.value)}
              className={`px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                months === opt.value
                  ? "bg-violet-500/20 text-violet-200 border border-violet-500/40"
                  : "text-slate-400 hover:bg-white/5 border border-transparent"
              }`}
              data-testid={`category-period-${opt.value}`}
            >
              {lang === "es" ? opt.label_es : opt.label_ca}
            </button>
          ))}
        </div>
      </div>

      {loading && segments.length === 0 ? (
        <div className="h-32 flex items-center justify-center text-slate-500 text-sm">
          {lang === "es" ? "Cargando..." : "Carregant..."}
        </div>
      ) : segments.length === 0 ? (
        <div className="h-24 flex flex-col items-center justify-center text-center">
          <Tag size={20} className="text-slate-600 mb-2" />
          <p className="text-xs text-slate-500">
            {lang === "es"
              ? "Sin gastos categorizados en este periodo"
              : "Sense despeses categoritzades en aquest període"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-5 items-center">
          {/* Donut chart (SVG) */}
          <div className="relative mx-auto" style={{ width: SIZE, height: SIZE }}>
            <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="-rotate-90">
              {/* Background ring */}
              <circle
                cx={center} cy={center} r={RADIUS}
                fill="none" stroke="rgba(255,255,255,0.06)"
                strokeWidth={STROKE}
              />
              {segments.map((s) => (
                <circle
                  key={s.idx}
                  cx={center} cy={center} r={RADIUS}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={hoverIdx === s.idx ? STROKE + 4 : STROKE}
                  strokeDasharray={`${s.dashLength} ${s.dashGap}`}
                  strokeDashoffset={s.offset}
                  strokeLinecap="butt"
                  className="transition-all duration-200 cursor-pointer"
                  onMouseEnter={() => setHoverIdx(s.idx)}
                  onMouseLeave={() => setHoverIdx(null)}
                  data-testid={`donut-segment-${s.idx}`}
                />
              ))}
            </svg>
            {/* Center label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {hovered
                  ? hovered.label
                  : (lang === "es" ? "Total" : "Total")}
              </div>
              <div className="text-lg font-bold text-white font-mono mt-0.5">
                {fmtEur(hovered ? hovered.total : data.total_current)}
              </div>
              {hovered && (
                <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                  {hovered.pct_of_total}%
                </div>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="space-y-1.5">
            {segments.map((s) => (
              <div
                key={s.idx}
                onMouseEnter={() => setHoverIdx(s.idx)}
                onMouseLeave={() => setHoverIdx(null)}
                className={`flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-colors ${
                  hoverIdx === s.idx ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                }`}
                data-testid={`category-legend-${s.idx}`}
              >
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: s.color }} />
                <span className="flex-1 text-xs text-slate-200 truncate capitalize">
                  {s.label}
                </span>
                <span className="text-[10px] text-slate-500 font-mono w-10 text-right">
                  {s.pct_of_total}%
                </span>
                <span className="text-xs text-white font-mono font-semibold w-20 text-right">
                  {fmtEur(s.total)}
                </span>
                <DeltaBadge delta_pct={s.delta_pct} is_new={s.is_new} lang={lang} />
              </div>
            ))}
          </div>
        </div>
      )}
      <BudgetsDialog
        open={showBudgets}
        onClose={() => { setShowBudgets(false); load(); }}
        suggestedCategories={data.rows.map((r) => r.category).filter(Boolean)}
      />
    </div>
  );
}

function DeltaBadge({ delta_pct, is_new, lang }) {
  if (is_new) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-violet-500/15 text-violet-300 border border-violet-500/30 w-16 justify-center flex-shrink-0">
        <Sparkles size={9} />
        {lang === "es" ? "Nuevo" : "Nou"}
      </span>
    );
  }
  if (!delta_pct || Math.abs(delta_pct) < 0.5) {
    return <span className="w-16 text-[10px] text-slate-500 text-center font-mono flex-shrink-0">—</span>;
  }
  const positive = delta_pct > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold w-16 justify-center flex-shrink-0 ${
      positive ? "bg-rose-500/10 text-rose-300" : "bg-emerald-500/10 text-emerald-300"
    }`}>
      <Icon size={9} />
      {positive ? "+" : ""}{delta_pct}%
    </span>
  );
}
